import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import SeoRobots from "@/components/SeoRobots";
import Index from "./pages/Index";
import Pricing from "./pages/Pricing";
import Industries from "./pages/Industries";
import About from "./pages/About";
import Imprint from "./pages/Imprint";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import Configurator from "./pages/Configurator";
import HelpCenter from "./pages/HelpCenter";
import HelpArticle from "./pages/HelpArticle";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Status from "./pages/Status";

// Portal (lazy – nicht SEO-relevant, reduziert Initial-Bundle)
const PortalLayout       = lazy(() => import("./components/portal/PortalLayout"));
const PortalLogin        = lazy(() => import("./pages/portal/PortalLogin"));
const ResetPassword      = lazy(() => import("./pages/portal/ResetPassword"));
const Dashboard          = lazy(() => import("./pages/portal/Dashboard"));
const MyModules          = lazy(() => import("./pages/portal/MyModules"));
const Invoices           = lazy(() => import("./pages/portal/Invoices"));
const PortalSettings     = lazy(() => import("./pages/portal/PortalSettings"));
const Support            = lazy(() => import("./pages/portal/Support"));
const CheckoutSuccess    = lazy(() => import("./pages/portal/CheckoutSuccess"));

// Admin (lazy)
const AdminProtectedRoute = lazy(() => import("./components/admin/AdminProtectedRoute"));
const AdminLayout         = lazy(() => import("./components/admin/AdminLayout"));
const AdminOrders         = lazy(() => import("./pages/admin/AdminOrders"));
const AdminSubscriptions  = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminCustomers      = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminBlog           = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor     = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminDashboard      = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStatus         = lazy(() => import("./pages/admin/AdminStatus"));
const AdminActivity       = lazy(() => import("./pages/admin/AdminActivity"));
const AdminEmailLog       = lazy(() => import("./pages/admin/AdminEmailLog"));
const AdminPlaybooks      = lazy(() => import("./pages/admin/AdminPlaybooks"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <ConsentProvider>
          <CookieBanner />
          <SeoRobots />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/features" element={<Navigate to="/konfigurator" replace />} />
            <Route path="/preise" element={<Pricing />} />
            <Route path="/branchen" element={<Industries />} />
            <Route path="/ueber-uns" element={<About />} />
            <Route path="/impressum" element={<Imprint />} />
            <Route path="/datenschutz" element={<Privacy />} />
            <Route path="/agb" element={<Terms />} />
            <Route path="/konfigurator" element={<Configurator />} />
            <Route path="/hilfe" element={<HelpCenter />} />
            <Route path="/hilfe/:slug" element={<HelpArticle />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/status" element={<Status />} />

            {/* Auth */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Portal (protected) */}
            <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="checkout-success" element={<CheckoutSuccess />} />
              <Route path="modules" element={<MyModules />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="settings" element={<PortalSettings />} />
              <Route path="support" element={<Support />} />
            </Route>

            {/* Admin (protected, admin role required) */}
            <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/neu" element={<AdminBlogEditor />} />
              <Route path="blog/:id" element={<AdminBlogEditor />} />
              <Route path="status" element={<AdminStatus />} />
              <Route path="playbooks" element={<AdminPlaybooks />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="emails" element={<AdminEmailLog />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </ConsentProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
